import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Rocket, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function NewCampaign() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [voteThreshold, setVoteThreshold] = useState(100);
  const [startAsPreorder, setStartAsPreorder] = useState(false);
  const [preOrderPrice, setPreOrderPrice] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [estimatedShipDate, setEstimatedShipDate] = useState("");
  const [preOrderCap, setPreOrderCap] = useState("");
  const [endDate, setEndDate] = useState("");
  const [referralDiscountPercent, setReferralDiscountPercent] = useState("");
  const [referralBonusThreshold, setReferralBonusThreshold] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.listProducts().then((p) => {
      setProducts(p);
      if (p.length > 0) setProductId(p[0].id);
    });
  }, []);

  const selectedProduct = products.find((p) => p.id === productId);

  useEffect(() => {
    if (selectedProduct && !preOrderPrice) {
      setPreOrderPrice(String(selectedProduct.priceRange.min));
    }
    // eslint-disable-next-line
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (startAsPreorder && !estimatedShipDate) {
        toast.error("Estimated ship date is required for a pre-order");
        setSubmitting(false);
        return;
      }
      const payload = {
        productId,
        startAsPreorder,
        voteThreshold: startAsPreorder ? null : Number(voteThreshold),
        preOrderPrice: preOrderPrice ? Number(preOrderPrice) : null,
        depositAmount: depositAmount ? Number(depositAmount) : null,
        estimatedShipDate: estimatedShipDate || null,
        preOrderCap: preOrderCap ? Number(preOrderCap) : null,
        endDate: endDate || null,
        referralDiscountPercent: referralDiscountPercent ? Number(referralDiscountPercent) : null,
        referralBonusThreshold: referralBonusThreshold ? Number(referralBonusThreshold) : null,
        videoUrl: videoUrl.trim() || null,
      };
      const c = await api.createCampaign(payload);
      toast.success("Campaign created");
      navigate(`/app/campaigns/${c.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create campaign");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="new-campaign-page">
      <div>
        <Link to="/app" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Create campaign</h1>
        <p className="text-sm text-gray-600 mt-2">Pick a product, set a launch threshold, and configure pre-order settings.</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-6">
          <section className="lp-card p-6">
            <h2 className="text-base font-semibold tracking-tight mb-1">Product</h2>
            <p className="text-xs text-gray-500 mb-5">Select an upcoming product (draft/active) from your store.</p>
            <label className="lp-label" htmlFor="product-picker">Product</label>
            <select
              id="product-picker"
              className="lp-input"
              data-testid="product-picker"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} — {p.vendor}
                </option>
              ))}
            </select>

            {selectedProduct && (
              <div className="mt-5 flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <img
                  src={selectedProduct.imageUrl}
                  alt=""
                  className="w-16 h-16 object-cover rounded-lg border border-gray-100"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{selectedProduct.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{selectedProduct.vendor}</div>
                  <div className="text-xs text-gray-700 mt-1">
                    Listed price ${selectedProduct.priceRange.min.toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className="lp-card p-6" data-testid="campaign-mode-section">
            <h2 className="text-base font-semibold tracking-tight mb-1">How do you want to launch?</h2>
            <p className="text-xs text-gray-500 mb-5">Collect votes first, or open pre-orders right away.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                data-testid="mode-voting"
                onClick={() => setStartAsPreorder(false)}
                className={`text-left p-4 rounded-xl border transition-all ${!startAsPreorder ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-300"}`}
              >
                <div className="font-medium text-sm text-gray-900">Vote to launch</div>
                <div className="text-xs text-gray-500 mt-1">Customers vote. Auto-flips to pre-order when the threshold is hit.</div>
              </button>
              <button
                type="button"
                data-testid="mode-preorder"
                onClick={() => setStartAsPreorder(true)}
                className={`text-left p-4 rounded-xl border transition-all ${startAsPreorder ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-300"}`}
              >
                <div className="font-medium text-sm text-gray-900">Pre-order now</div>
                <div className="text-xs text-gray-500 mt-1">Skip voting — the product goes live for pre-order immediately.</div>
              </button>
            </div>
          </section>

          {!startAsPreorder && (
          <section className="lp-card p-6">
            <p className="text-xs text-gray-500 mb-5">When the threshold is hit, the campaign auto-flips to pre-order.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="lp-label" htmlFor="vote-threshold">Launch threshold</label>
                <input
                  id="vote-threshold"
                  type="number"
                  min="1"
                  className="lp-input"
                  data-testid="vote-threshold-input"
                  value={voteThreshold}
                  onChange={(e) => setVoteThreshold(e.target.value)}
                  required
                />
                <div className="lp-help">Supporters needed to unlock pre-order.</div>
              </div>
              <div>
                <label className="lp-label" htmlFor="end-date">Campaign end date (optional)</label>
                <input
                  id="end-date"
                  type="date"
                  className="lp-input"
                  data-testid="end-date-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </section>
          )}

          <section className="lp-card p-6">
            <h2 className="text-base font-semibold tracking-tight mb-1">Pre-order settings</h2>
            <p className="text-xs text-gray-500 mb-5">Configure how the product sells once it flips to pre-order.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="lp-label" htmlFor="preorder-price">Pre-order price ($)</label>
                <input
                  id="preorder-price"
                  type="number"
                  step="0.01"
                  className="lp-input"
                  data-testid="preorder-price-input"
                  value={preOrderPrice}
                  onChange={(e) => setPreOrderPrice(e.target.value)}
                />
              </div>
              <div>
                <label className="lp-label" htmlFor="deposit">Deposit amount ($, optional)</label>
                <input
                  id="deposit"
                  type="number"
                  step="0.01"
                  className="lp-input"
                  data-testid="deposit-input"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                />
                <div className="lp-help">Leave empty for full payment.</div>
              </div>
              <div>
                <label className="lp-label" htmlFor="ship-date">Estimated ship date</label>
                <input
                  id="ship-date"
                  type="date"
                  className="lp-input"
                  data-testid="ship-date-input"
                  value={estimatedShipDate}
                  onChange={(e) => setEstimatedShipDate(e.target.value)}
                />
              </div>
              <div>
                <label className="lp-label" htmlFor="cap">Pre-order cap (optional)</label>
                <input
                  id="cap"
                  type="number"
                  className="lp-input"
                  data-testid="preorder-cap-input"
                  value={preOrderCap}
                  onChange={(e) => setPreOrderCap(e.target.value)}
                />
                <div className="lp-help">Max number of pre-orders accepted.</div>
              </div>
            </div>
          </section>

          <section className="lp-card p-6">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-base font-semibold tracking-tight">Product video</h2>
              <span className="lp-badge lp-badge-voting">Optional</span>
            </div>
            <p className="text-xs text-gray-500 mb-5">
              Paste a YouTube, Vimeo, or direct MP4 link to show a video on the product page.
              Leave blank to use the video uploaded to this product in Shopify Admin (auto-detected).
            </p>
            <div>
              <label className="lp-label" htmlFor="video-url">Video URL</label>
              <input
                id="video-url"
                type="url"
                className="lp-input"
                data-testid="video-url-input"
                placeholder="https://youtu.be/dQw4w9WgXcQ"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
              <div className="lp-help">
                {selectedProduct?.videoUrl
                  ? "✓ Shopify product video detected — paste a URL above only to override."
                  : "No Shopify product video found for this item. Paste a link above if you want one."}
              </div>
            </div>
          </section>

          <section className="lp-card p-6">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-base font-semibold tracking-tight">Referral bonus</h2>
              <span className="lp-badge lp-badge-preorder">New</span>
            </div>
            <p className="text-xs text-gray-500 mb-5">
              Reward supporters who bring friends. Top referrers earn a discount on their pre-order.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="lp-label" htmlFor="ref-bonus-threshold">Referrals to unlock discount</label>
                <input
                  id="ref-bonus-threshold"
                  type="number"
                  min="1"
                  className="lp-input"
                  data-testid="ref-threshold-input"
                  value={referralBonusThreshold}
                  onChange={(e) => setReferralBonusThreshold(e.target.value)}
                  placeholder="3"
                />
                <div className="lp-help">Min number of friends a supporter must refer.</div>
              </div>
              <div>
                <label className="lp-label" htmlFor="ref-discount-pct">Discount percent</label>
                <input
                  id="ref-discount-pct"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  className="lp-input"
                  data-testid="ref-discount-input"
                  value={referralDiscountPercent}
                  onChange={(e) => setReferralDiscountPercent(e.target.value)}
                  placeholder="10"
                />
                <div className="lp-help">Applied to pre-order price for eligible referrers.</div>
              </div>
            </div>
            <div className="lp-help mt-3">Leave both empty to disable the referral program.</div>
          </section>
        </div>

        {/* Right: summary */}
        <aside className="space-y-6">
          <div className="lp-card p-6 sticky top-6">
            <div className="lp-section-label">Summary</div>
            <h3 className="text-lg font-medium tracking-tight mt-2">
              {selectedProduct?.title || "Pick a product"}
            </h3>
            <div className="mt-5 space-y-3 text-sm">
              <Row label="Mode" value={startAsPreorder ? "Pre-order now" : "Vote to launch"} />
              {!startAsPreorder && <Row label="Threshold" value={`${voteThreshold} supporters`} />}
              <Row label="Pre-order price" value={preOrderPrice ? `$${Number(preOrderPrice).toFixed(2)}` : "—"} />
              <Row label="Deposit" value={depositAmount ? `$${Number(depositAmount).toFixed(2)}` : "Full payment"} />
              <Row label="Ship date" value={estimatedShipDate || "—"} />
              <Row label="Cap" value={preOrderCap ? `${preOrderCap}` : "Unlimited"} />
              <Row
                label="Referral bonus"
                value={
                  referralDiscountPercent && referralBonusThreshold
                    ? `${referralDiscountPercent}% off @ ${referralBonusThreshold} refs`
                    : "Disabled"
                }
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !productId}
              className="lp-btn-primary w-full justify-center mt-6"
              data-testid="submit-campaign-btn"
            >
              {submitting ? "Creating…" : startAsPreorder ? (<><Rocket size={16} /> Open pre-order</>) : (<><Rocket size={16} /> Launch campaign</>)}
            </button>
            <div className="text-[11px] text-gray-500 mt-3 text-center">
              You can edit these settings any time.
            </div>
          </div>

          <div className="lp-card p-6 bg-gradient-to-br from-gray-50 to-white">
            <div className="flex items-center gap-2 mb-2">
              <Check size={14} className="text-emerald-600" />
              <div className="text-xs font-semibold">What happens next</div>
            </div>
            {startAsPreorder ? (
              <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside leading-relaxed">
                <li>The product goes live for pre-order immediately — no voting.</li>
                <li>Embed the Launch widget on the product page.</li>
                <li>Customers pre-order and check out via Shopify.</li>
                <li>Fulfil pre-orders from the campaign dashboard when ready.</li>
              </ol>
            ) : (
              <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside leading-relaxed">
                <li>Embed the Launch widget on the product page.</li>
                <li>Customers click "Launch this" with their email (one per product).</li>
                <li>Each supporter gets a unique referral link to share.</li>
                <li>At threshold, status flips to pre-order automatically.</li>
                <li>Top referrers see a discount applied at checkout.</li>
              </ol>
            )}
          </div>
        </aside>
      </form>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);
